import { useState } from "react";
import { UserPlus, Trash2, Users, Pause, Play, Info, Send, KeyRound, Copy, Check, X, Eye } from "lucide-react";
import { Avatar, Panel, EmptyState, inputCls, btnPrimary } from "./crm";
import { CalendlyBadge } from "./CalendlySheet";

const LOGIN_STATUS = {
  none: { label: "Not invited", cls: "border-slate-200 bg-slate-50 text-slate-500" },
  invited: { label: "Invited", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  active: { label: "Active", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
};

export function TempCodeDialog({ info, onClose, portal = "/agent" }) {
  const [copied, setCopied] = useState(false);
  if (!info) return null;
  const copy = () => { navigator.clipboard?.writeText(info.temp_password); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/40 sm:px-4" onClick={onClose} data-testid="temp-code-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-slate-200 bg-white p-6 pb-safe shadow-2xl">
        <div className="flex items-start justify-between">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><KeyRound className="h-5 w-5" /></span>
          <button onClick={onClose} data-testid="temp-code-close" className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <h2 className="mt-4 text-lg font-extrabold text-slate-900">Share this access code with {info.name}</h2>
        <p className="mt-1 text-sm text-slate-500">The invite e-mail to <span className="font-semibold text-slate-700">{info.email}</span> couldn't be delivered, so here is the one-time access code. They sign in at <span className="font-mono-num">{portal}</span> and will be asked to set their own password.</p>
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <code className="font-mono-num flex-1 text-base font-bold tracking-wider text-slate-900" data-testid="temp-code-value">{info.temp_password}</code>
          <button onClick={copy} data-testid="temp-code-copy" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300">{copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}</button>
        </div>
        <p className="mt-3 text-[11px] text-slate-400">This code is shown only once. You can generate a new one any time with "Resend invite".</p>
      </div>
    </div>
  );
}

export default function AgentsView({ agents, leaderboard = [], onAdd, onInvite, onToggle, onRemove, onViewAs }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [tempInfo, setTempInfo] = useState(null);

  const perf = Object.fromEntries(leaderboard.map((r) => [r.id, r]));
  const active = agents.filter((a) => a.active);

  const showCodeIfNeeded = (agent, res) => {
    if (res && !res.invite_sent && res.temp_password) setTempInfo({ name: agent.name, email: agent.email, temp_password: res.temp_password });
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Enter the agent's full name (at least 2 characters).";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid e-mail address.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const res = await onAdd({ name: form.name.trim(), email: form.email.trim().toLowerCase() });
      if (res) { setForm({ name: "", email: "" }); showCodeIfNeeded(res, res); }
    } finally {
      setBusy(false);
    }
  };

  const invite = async (agent) => {
    setInviting(agent.id);
    try {
      const res = await onInvite(agent);
      showCodeIfNeeded(agent, res);
    } finally {
      setInviting(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="agents-view">
      <div>
        <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Agents</h1>
        <p className="mt-1 text-sm text-slate-500"><span className="font-mono-num font-semibold text-slate-900">{active.length}</span> of {agents.length} agents in the round-robin rotation</p>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel title="Add agent" subtitle="New agents join the rotation immediately" testid="admin-add-agent-form">
          <form onSubmit={submit} noValidate className="space-y-4 p-5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700">Full name</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-agent-name-input" placeholder="e.g. Brandon Beal" className={`${inputCls} ${errors.name ? "border-rose-400" : ""}`} />
              {errors.name && <p className="mt-1 text-xs text-rose-600" data-testid="agent-name-error">{errors.name}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700">E-mail (receives lead alerts)</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-agent-email-input" placeholder="agent@breezewealthpartners.com" className={`${inputCls} ${errors.email ? "border-rose-400" : ""}`} />
              {errors.email && <p className="mt-1 text-xs text-rose-600" data-testid="agent-email-error">{errors.email}</p>}
            </div>
            <button type="submit" disabled={busy} data-testid="admin-add-agent-button" className={`${btnPrimary} w-full`}><UserPlus className="h-4 w-4" /> {busy ? "Adding…" : "Add Agent & Send Invite"}</button>
            <p className="flex items-start gap-2 text-[11px] text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> The agent receives an e-mail with a one-time access code for the agent portal and joins the round-robin rotation immediately. Paused agents are skipped but keep their history.</p>
          </form>
        </Panel>

        <Panel title="Roster" subtitle="Rotation order and performance" className="order-first xl:order-none xl:col-span-2 overflow-hidden" testid="admin-agents-list">
          {agents.length === 0 ? (
            <EmptyState icon={Users} title="No agents in the roster" hint="Add your first agent to start routing recruits." />
          ) : (
            <>
            {/* Card list (phones) */}
            <ul className="divide-y divide-slate-100 md:hidden" data-testid="agents-card-list">
              {agents.map((a, i) => {
                const p = perf[a.id] || {};
                const ls = LOGIN_STATUS[a.login_status] || LOGIN_STATUS.none;
                return (
                  <li key={a.id} className="px-4 py-3.5" data-testid={`agent-card-${a.id}`}>
                    <div className="flex items-center gap-3">
                      <Avatar name={a.name} size="md" className={a.active ? "" : "opacity-50"} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono-num text-[10px] font-bold text-slate-400">#{i + 1}</span>
                          <span className="truncate text-[15px] font-semibold text-slate-900">{a.name}</span>
                        </div>
                        <div className="truncate text-xs text-slate-500">{a.email}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-semibold ${a.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-600"}`}><span className={`h-1.5 w-1.5 rounded-full ${a.active ? "bg-emerald-500" : "bg-slate-400"}`} />{a.active ? "In rotation" : "Paused"}</span>
                      <span className={`inline-flex items-center rounded-md border px-2 py-0.5 font-semibold ${ls.cls}`}>Login: {ls.label}</span>
                      <span className="ml-auto font-mono-num text-slate-500"><b className="text-slate-900">{p.assigned ?? a.leads_assigned ?? 0}</b> assigned · <b className="text-emerald-700">{p.onboarded ?? 0}</b> onboarded</span>
                    </div>
                    <div className="mt-3 grid grid-cols-4 gap-2">
                      <button onClick={() => onViewAs(a)} data-testid={`admin-agent-viewas-mobile-${a.id}`} className="flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 text-xs font-semibold text-sky-700 active:bg-sky-100"><Eye className="h-3.5 w-3.5" /> View as</button>
                      <button onClick={() => invite(a)} disabled={inviting === a.id} className="flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 active:bg-slate-50 disabled:opacity-50"><Send className="h-3.5 w-3.5" /> {inviting === a.id ? "Sending…" : a.login_status === "none" ? "Invite" : "Resend"}</button>
                      <button onClick={() => onToggle(a)} className="flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 active:bg-slate-50">{a.active ? <><Pause className="h-3.5 w-3.5" /> Pause</> : <><Play className="h-3.5 w-3.5" /> Activate</>}</button>
                      {confirmId === a.id ? (
                        <button onClick={() => { onRemove(a); setConfirmId(null); }} className="flex min-h-[40px] items-center justify-center rounded-lg bg-rose-600 text-xs font-semibold text-white">Confirm</button>
                      ) : (
                        <button onClick={() => setConfirmId(a.id)} className="flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 active:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 text-left font-semibold">#</th>
                    <th className="px-3 py-3 text-left font-semibold">Agent</th>
                    <th className="px-3 py-3 text-left font-semibold">Rotation</th>
                    <th className="px-3 py-3 text-left font-semibold">Portal login</th>
                    <th className="px-3 py-3 text-right font-semibold">Assigned</th>
                    <th className="px-3 py-3 text-right font-semibold">Onboarded</th>
                    <th className="px-5 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {agents.map((a, i) => {
                    const p = perf[a.id] || {};
                    return (
                      <tr key={a.id} data-testid={`admin-agent-${a.id}`} className={`${a.active ? "" : "bg-slate-50/60"} hover:bg-slate-50/80`}>
                        <td className="font-mono-num px-5 py-3 text-xs font-bold text-slate-400">{i + 1}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={a.name} size="sm" className={a.active ? "" : "opacity-50"} />
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-slate-900">{a.name}</div>
                              <div className="truncate text-xs text-slate-500">{a.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${a.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-600"}`} data-testid={`agent-status-${a.id}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${a.active ? "bg-emerald-500" : "bg-slate-400"}`} /> {a.active ? "Active" : "Paused"}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          {(() => { const ls = LOGIN_STATUS[a.login_status] || LOGIN_STATUS.none; return (
                            <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${ls.cls}`} data-testid={`agent-login-status-${a.id}`}>{ls.label}</span>
                          ); })()}
                          <div className="mt-1"><CalendlyBadge status={a.calendly?.status === "connected" && !a.calendly?.event_type_uri ? "event_type_missing" : a.calendly?.status || "not_connected"} /></div>
                        </td>
                        <td className="font-mono-num px-3 py-3 text-right text-slate-700">{p.assigned ?? a.leads_assigned ?? 0}</td>
                        <td className="font-mono-num px-3 py-3 text-right font-bold text-emerald-700">{p.onboarded ?? 0}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => onViewAs(a)} data-testid={`admin-agent-viewas-${a.id}`} title="Open the agent portal as this agent (you stay signed in as admin)" className="inline-flex items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 transition-colors">
                              <Eye className="h-3 w-3" /> View as
                            </button>
                            <button onClick={() => invite(a)} disabled={inviting === a.id} data-testid={`admin-agent-invite-${a.id}`} title={a.login_status === "none" ? "Send portal invite" : "Resend invite / reset access code"} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-sky-300 hover:text-sky-700 transition-colors disabled:opacity-50">
                              <Send className="h-3 w-3" /> {inviting === a.id ? "Sending…" : a.login_status === "none" ? "Invite" : "Resend"}
                            </button>
                            <button onClick={() => onToggle(a)} data-testid={`admin-agent-toggle-${a.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors">
                              {a.active ? <><Pause className="h-3 w-3" /> Pause</> : <><Play className="h-3 w-3" /> Activate</>}
                            </button>
                            {confirmId === a.id ? (
                              <span className="inline-flex items-center gap-1">
                                <button onClick={() => { onRemove(a); setConfirmId(null); }} data-testid={`admin-agent-confirm-delete-${a.id}`} className="rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700">Remove</button>
                                <button onClick={() => setConfirmId(null)} data-testid={`admin-agent-cancel-delete-${a.id}`} className="rounded-md border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
                              </span>
                            ) : (
                              <button onClick={() => setConfirmId(a.id)} data-testid={`admin-agent-delete-${a.id}`} title="Remove agent" className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-500 hover:border-rose-300 hover:text-rose-600 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </Panel>
      </div>
      <TempCodeDialog info={tempInfo} onClose={() => setTempInfo(null)} />
    </div>
  );
}
